import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, X, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILES = 5;
const MAX_MB = 10;
const ACCEPT = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

export const ImageUpload = ({ files, onChange }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const addFiles = async (list) => {
    const incoming = Array.from(list);
    const room = MAX_FILES - files.length;
    if (room <= 0) return toast.error(`Maximal ${MAX_FILES} Bilder pro Anfrage.`);
    const selected = incoming.slice(0, room);
    if (incoming.length > room) toast.error(`Nur ${room} weitere Bild(er) möglich – maximal ${MAX_FILES}.`);

    for (const file of selected) {
      if (!ACCEPT.includes(file.type)) { toast.error(`${file.name}: Nur JPG, PNG, WEBP oder HEIC.`); continue; }
      if (file.size > MAX_MB * 1024 * 1024) { toast.error(`${file.name}: Datei größer als ${MAX_MB} MB.`); continue; }
      const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const preview = URL.createObjectURL(file);
      onChange((prev) => [...prev, { localId, name: file.name, size: file.size, preview, status: "uploading", id: null }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange((prev) => prev.map((f) => (f.localId === localId ? { ...f, id: data.id, status: "done" } : f)));
      } catch (err) {
        const d = err.response?.data?.detail;
        toast.error(typeof d === "string" ? d : `${file.name}: Upload fehlgeschlagen.`);
        onChange((prev) => prev.filter((f) => f.localId !== localId));
      }
    }
  };

  const remove = (localId) => onChange((prev) => prev.filter((f) => f.localId !== localId));

  return (
    <div className="sm:col-span-2" data-testid="image-upload">
      <label className="eyebrow !text-[0.6rem] block mb-2">Produktfotos (optional, max. {MAX_FILES} × {MAX_MB} MB)</label>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        className={`field cursor-pointer flex items-center gap-4 !py-5 border-dashed transition-colors ${drag ? "!border-[var(--gold)] bg-[rgba(212,175,55,0.06)]" : ""}`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        data-testid="image-upload-dropzone"
      >
        <span className="w-10 h-10 shrink-0 border border-[var(--line-strong)] flex items-center justify-center text-[var(--gold)]"><ImagePlus size={18} /></span>
        <span className="text-sm text-[var(--muted)]">
          <span className="text-[var(--text)]">Produktfoto hochladen</span> oder hierher ziehen – damit Sie sofort ein passgenaues Angebot erhalten.
        </span>
        <input ref={inputRef} type="file" accept={ACCEPT.join(",")} multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="image-upload-input" />
      </div>

      {files.length > 0 && (
        <ul className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="image-upload-list">
          {files.map((f) => (
            <li key={f.localId} className="relative aspect-square border border-[var(--line)] bg-[var(--bg-2)] overflow-hidden group" data-testid="image-upload-item">
              <img src={f.preview} alt={f.name} className="w-full h-full object-cover" />
              <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${f.status === "uploading" ? "bg-black/60" : "bg-black/0"}`}>
                {f.status === "uploading" ? <Loader2 size={20} className="text-[var(--gold)] animate-spin" /> : <CheckCircle2 size={18} className="text-[var(--gold)] absolute bottom-1.5 left-1.5 drop-shadow" />}
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(f.localId); }} className="absolute top-1.5 right-1.5 w-6 h-6 bg-[rgba(10,10,11,0.9)] border border-[var(--line-strong)] text-[var(--text)] flex items-center justify-center hover:bg-[var(--gold)] hover:text-[#0a0a0b] transition-colors" aria-label="Entfernen" data-testid="image-upload-remove">
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
